if (!customElements.get('auto-swiper')) {
  customElements.define('auto-swiper', class AutoSwiper extends HTMLElement {
    constructor() {
      super();
      this.toBool = (v) => String(v) === 'true';
      this.toNumber = (v, fallback = 0) => (Number.isFinite(Number(v)) ? Number(v) : fallback);
    }

    async connectedCallback() {
      if (this.dataset.mounted) return;
      this.dataset.mounted = 'loading';

      this.enableAutoPlay = this.toBool(this.dataset.autoPlay);
      this.swiperTime = this.toNumber(this.dataset.swiperTime, 6000);
      this.slidesPerView = this.toNumber(this.dataset.slidesPerView, 1);
      this.mobileSlideGap = this.toNumber(this.dataset.mobileSlideGap, 12);
      this.slidesFullWidth = this.toBool(this.dataset.slidesFullWidth);
      this.paginateFixed = this.toBool(this.dataset.paginationFixed);
      this.PAUSE_CLASS = 'pagination-paused-by-js';
      this.isPlaying = this.enableAutoPlay;
      this.isInViewport = false;

      // 使用 this.querySelector 替代旧版的拼接 ID
      this.container = this.querySelector('.myNewSwiper');
      this.swiperContain = this.querySelector('.new-swiper-contain');
      this.paginationWrap = this.querySelector('.swiper-pagination-wrap');
      this.paginationEl = this.querySelector('.new-swiper-pagination');
      this.titleElement = this.querySelector('.new-swiper-title-wrap > .new-swiper-title');
      this.descriptionElement = this.querySelector('.new-swiper-title-wrap > .new-swiper-content');
      this.extraContentElement = this.querySelector('.new-swiper-title-wrap > .new-swiper-extra-content');
      this.playPauseBtn = this.querySelector('.swiper-playpause');
      this.bottomPrevBtn = this.querySelector('[data-bottom-arrow="prev"]');
      this.bottomNextBtn = this.querySelector('[data-bottom-arrow="next"]');

      if (!this.container) {
        delete this.dataset.mounted;
        return;
      }

      try {
        await window.loadSwiper();
      } catch (error) {
        delete this.dataset.mounted;
        console.error('[new-swiper-optimize] Unable to load Swiper.', error);
        return;
      }

      if (!this.isConnected) {
        delete this.dataset.mounted;
        return;
      }

      this.initSwiper();
      this.bindControls();
      this.maybeBindObserver();
      this.dataset.mounted = 'true';
    }

    disconnectedCallback() {
      this.viewportObserver?.disconnect();
    }

    updateTexts() {
      if (!this.titleElement && !this.descriptionElement && !this.extraContentElement) return;
      const activeSlide = this.swiper?.slides?.[this.swiper.activeIndex];
      if (!activeSlide) return;

      const newTitle = activeSlide.getAttribute('data-title') || '';
      const newDescription = activeSlide.getAttribute('data-description') || '';
      const newExtraContent = activeSlide.getAttribute('data-extra-content') || '';

      if (this.titleElement) this.titleElement.style.opacity = '0';
      if (this.descriptionElement) this.descriptionElement.style.opacity = '0';
      if (this.extraContentElement) this.extraContentElement.style.opacity = '0';

      window.setTimeout(() => {
        if (this.titleElement) {
          this.titleElement.textContent = newTitle;
          this.titleElement.style.opacity = '1';
        }
        if (this.descriptionElement) {
          this.descriptionElement.textContent = newDescription;
          this.descriptionElement.style.opacity = '1';
        }
        if (this.extraContentElement) {
          this.extraContentElement.innerHTML = newExtraContent;
          this.extraContentElement.style.opacity = '1';
        }
      }, 500);
    }

    setBottomArrowDisabled(button, disabled) {
      if (!button) return;
      button.disabled = disabled;
      button.setAttribute('aria-disabled', String(disabled));
      button.classList.toggle('is-disabled', disabled);
      button.tabIndex = disabled ? -1 : 0;
    }

    /**
     * 同步底部箭头 disabled：首尾不可切换、内容不足（locked）时置灰。
     * slidesPerView:auto / 图片加载后尺寸变化时也需重算，故多事件触发。
     */
    updateBottomArrowState(swiper = this.swiper) {
      if (!swiper || (!this.bottomPrevBtn && !this.bottomNextBtn)) return;

      const slideCount = swiper.slides?.length || 0;
      const isLocked = Boolean(swiper.isLocked) || slideCount <= 1;
      const atStart = isLocked || swiper.isBeginning || swiper.progress <= 0;
      const atEnd = isLocked || swiper.isEnd || swiper.progress >= 1;

      this.setBottomArrowDisabled(this.bottomPrevBtn, atStart);
      this.setBottomArrowDisabled(this.bottomNextBtn, atEnd);
    }

    togglePaginationPausedClass(forcePause) {
      if (!this.paginationEl) return;
      this.paginationEl.classList.toggle(this.PAUSE_CLASS, forcePause);
    }

    resetPaginationAnimation() {
      const active = this.querySelector('.swiper-pagination-bullet-active');
      if (!active) return;
      active.style.animation = 'none';
      active.offsetHeight; // force reflow
      active.style.animation = '';
    }

    updatePlayPauseUI() {
      if (!this.playPauseBtn) return;
      const playIcon = this.playPauseBtn.querySelector('.play-btn');
      const pauseIcon = this.playPauseBtn.querySelector('.pause-btn');

      if (playIcon && pauseIcon) {
        pauseIcon.style.display = this.isPlaying ? 'block' : 'none';
        playIcon.style.display = this.isPlaying ? 'none' : 'block';
      }

      this.playPauseBtn.setAttribute('aria-pressed', String(this.isPlaying));
      this.playPauseBtn.classList.toggle('is-paused', !this.isPlaying);
    }

    shouldAutoPlay() {
      return this.enableAutoPlay && this.isPlaying && this.isInViewport;
    }

    syncAutoPlay() {
      if (!this.swiper || !this.enableAutoPlay) {
        this.togglePaginationPausedClass(true);
        return;
      }

      if (this.shouldAutoPlay()) {
        try { this.swiper.autoplay.start(); } catch (e) {}
        this.togglePaginationPausedClass(false);
        this.resetPaginationAnimation();
      } else {
        try { this.swiper.autoplay.stop(); } catch (e) {}
        this.togglePaginationPausedClass(true);
      }
    }

    initSwiper() {
      const resolvedSlidesPerView = this.slidesFullWidth ? 1 : Math.max(this.slidesPerView, 1);
      const resolvedMobileSpaceBetween = this.slidesFullWidth ? 0 : Math.max(this.mobileSlideGap, 0);

      this.swiper = new window.Swiper(this.container, {
        slidesPerView: this.slidesFullWidth ? 1 : 'auto',
        loop: false,
        allowTouchMove: true,
        spaceBetween: resolvedMobileSpaceBetween,
        autoplay: this.enableAutoPlay
          ? { delay: this.swiperTime, disableOnInteraction: false }
          : false,
        pagination: {
          el: this.paginationEl,
          clickable: true,
        },
        navigation: {
          nextEl: this.querySelector('.swiper-button-next'),
          prevEl: this.querySelector('.swiper-button-prev'),
        },
        watchOverflow: true,
        on: {
          init: (swiper) => {
            this.updateBottomArrowState(swiper);
            this.updatePlayPauseUI();
            this.syncAutoPlay();
          },
          slideChange: () => {
            this.updateTexts();
            this.updateBottomArrowState();
            if (this.shouldAutoPlay()) this.resetPaginationAnimation();
          },
          slideChangeTransitionEnd: () => this.updateBottomArrowState(),
          reachBeginning: () => this.updateBottomArrowState(),
          reachEnd: () => this.updateBottomArrowState(),
          fromEdge: () => this.updateBottomArrowState(),
          toEdge: () => this.updateBottomArrowState(),
          lock: () => this.updateBottomArrowState(),
          unlock: () => this.updateBottomArrowState(),
          resize: () => this.updateBottomArrowState(),
          update: () => this.updateBottomArrowState(),
          imagesReady: () => this.updateBottomArrowState(),
        },
        breakpoints: {
          720: {
            slidesPerView: resolvedSlidesPerView,
            spaceBetween: this.slidesFullWidth ? 0 : 30,
            allowTouchMove: false,
          },
        },
      });

      this.syncAutoPlay();
      this.updateBottomArrowState();
      // 布局/图片未稳定时再补一次，避免首屏箭头状态不准
      requestAnimationFrame(() => this.updateBottomArrowState());
    }

    bindControls() {
      this.bottomPrevBtn?.addEventListener('click', () => {
        if (!this.bottomPrevBtn.disabled) this.swiper.slidePrev();
      });

      this.bottomNextBtn?.addEventListener('click', () => {
        if (!this.bottomNextBtn.disabled) this.swiper.slideNext();
      });

      this.playPauseBtn?.addEventListener('click', () => {
        this.isPlaying = !this.isPlaying;
        this.syncAutoPlay();
        this.updatePlayPauseUI();
      });
    }

    maybeBindObserver() {
      const observedElement = this.swiperContain || this;

      if (!('IntersectionObserver' in window)) {
        this.isInViewport = true;
        this.syncAutoPlay();
        return;
      }

      this.viewportObserver = new IntersectionObserver((entries) => {
        const entry = entries[0];
        this.isInViewport = entry.isIntersecting;
        this.syncAutoPlay();

        if (!this.paginateFixed || !this.paginationWrap) return;

        if (entry.isIntersecting) {
          this.paginationWrap.style.position = 'fixed';
          this.paginationWrap.style.display = 'flex';
          this.paginationWrap.classList.add('slide-up');
        } else {
          this.paginationWrap.style.display = 'none';
          this.paginationWrap.classList.remove('slide-up');
        }
      }, { threshold: 0.5 });

      this.viewportObserver.observe(observedElement);
    }
  });
}
